import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BottomSheet from '../mobile/BottomSheet'
import MaterialIcon from '../ui/MaterialIcon'
import { MealWellnessDisclaimer } from '../compliance/Disclaimer'
import { KILO } from '../../constants/KILO_THEME'
import { ONBOARDING, onboardingChipClass } from '../../lib/onboarding/formStyles'
import { supabase } from '../../lib/supabaseClient'

type NutritionResult = {
  food_name: string
  off_product_name?: string
  off_barcode?: string
  protein_100g: number
  carbs_100g: number
  fat_100g: number
  energy_kcal_100g: number
  fiber_100g: number
  image_url?: string
  data_source: string
}

type MealType = 'breakfast' | 'lunch' | 'dinner' | 'snack'

type UnplannedMealLoggerProps = {
  isOpen: boolean
  onClose: () => void
  onLogged: () => void
}

const MEAL_TYPES: MealType[] = ['breakfast', 'lunch', 'dinner', 'snack']

export function UnplannedMealLogger({ isOpen, onClose, onLogged }: UnplannedMealLoggerProps) {
  const { t } = useTranslation()
  const [mode, setMode] = useState<'search' | 'barcode'>('search')
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<NutritionResult[]>([])
  const [selected, setSelected] = useState<NutritionResult | null>(null)
  const [quantity, setQuantity] = useState('100')
  const [mealType, setMealType] = useState<MealType>('snack')
  const [step, setStep] = useState<'search' | 'confirm'>('search')
  const [error, setError] = useState('')
  const [logged, setLogged] = useState(false)
  const barcodeInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (isOpen) return
    setMode('search')
    setQuery('')
    setLoading(false)
    setResults([])
    setSelected(null)
    setQuantity('100')
    setMealType('snack')
    setStep('search')
    setError('')
    setLogged(false)
  }, [isOpen])

  async function invokeUnplanned(body: Record<string, unknown>) {
    const { data, error: invokeError } = await supabase.functions.invoke('log-unplanned-meal', { body })
    if (invokeError) throw new Error(invokeError.message)
    return data as Record<string, unknown>
  }

  async function handleSearch() {
    if (!query.trim()) return
    setLoading(true)
    setError('')
    setResults([])
    try {
      const data = await invokeUnplanned({ mode: 'search', query: query.trim() })
      const nextResults = (data.results as NutritionResult[] | undefined) ?? []
      setResults(nextResults)
      if (nextResults.length === 0) {
        setError(t('meals.unplanned.noResults'))
      }
    } catch {
      setError(t('meals.unplanned.searchFailed'))
    } finally {
      setLoading(false)
    }
  }

  async function handleBarcodeInput(barcode: string) {
    if (!barcode.trim() || barcode.length < 8) return
    setLoading(true)
    setError('')
    try {
      const data = await invokeUnplanned({ mode: 'barcode', barcode: barcode.trim() })
      if (data.found && data.result) {
        const result = data.result as NutritionResult
        setResults([result])
        setSelected(result)
        setStep('confirm')
      } else {
        setError(String(data.error ?? t('meals.unplanned.barcodeNotFound')))
      }
    } catch {
      setError(t('meals.unplanned.barcodeFailed'))
    } finally {
      setLoading(false)
    }
  }

  function selectResult(result: NutritionResult) {
    setSelected(result)
    setStep('confirm')
  }

  async function handleConfirm() {
    if (!selected) return
    const qty = parseFloat(quantity)
    if (Number.isNaN(qty) || qty <= 0) {
      setError(t('meals.unplanned.invalidQuantity'))
      return
    }
    setLoading(true)
    setError('')
    try {
      const data = await invokeUnplanned({
        mode: 'confirm',
        confirm_entry: {
          food_name: selected.food_name,
          quantity_g: qty,
          energy_kcal: selected.energy_kcal_100g,
          protein_g: selected.protein_100g,
          carbs_g: selected.carbs_100g,
          fat_g: selected.fat_100g,
          fiber_g: selected.fiber_100g,
          source: selected.data_source === 'cached' ? 'cached' : 'open_food_facts',
          off_product_name: selected.off_product_name,
          off_barcode: selected.off_barcode,
          meal_type: mealType,
          logged_at: new Date().toISOString(),
        },
      })
      if (data.status === 'logged') {
        setLogged(true)
        setTimeout(() => {
          onLogged()
          onClose()
        }, 1500)
      } else {
        setError(String(data.error ?? t('meals.unplanned.logFailed')))
      }
    } catch {
      setError(t('meals.unplanned.saveFailed'))
    } finally {
      setLoading(false)
    }
  }

  const ratio = parseFloat(quantity) / 100
  const computedMacros = useMemo(() => {
    if (!selected || Number.isNaN(ratio) || ratio <= 0) return null
    return {
      kcal: Math.round(selected.energy_kcal_100g * ratio),
      protein: Math.round(selected.protein_100g * ratio * 10) / 10,
      carbs: Math.round(selected.carbs_100g * ratio * 10) / 10,
      fat: Math.round(selected.fat_100g * ratio * 10) / 10,
    }
  }, [selected, ratio])

  return (
    <BottomSheet
      variant="dark"
      open={isOpen}
      onClose={onClose}
      title={step === 'search' ? t('meals.unplanned.title') : t('meals.unplanned.confirmTitle')}
    >
      <p className={`-mt-2 mb-4 text-xs ${KILO.text.muted}`}>
        {step === 'search' ? t('meals.unplanned.searchHint') : t('meals.unplanned.confirmHint')}
      </p>

      {step === 'search' ? (
        <>
          <div className="mb-4 grid grid-cols-2 gap-2">
            {(['search', 'barcode'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                className={onboardingChipClass(mode === m)}
              >
                {m === 'search' ? t('meals.unplanned.modeSearch') : t('meals.unplanned.modeBarcode')}
              </button>
            ))}
          </div>

          {mode === 'search' ? (
            <div className="mb-4 flex gap-2">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void handleSearch()}
                placeholder={t('meals.unplanned.searchPlaceholder')}
                className={`flex-1 ${ONBOARDING.input}`}
              />
              <button
                type="button"
                onClick={() => void handleSearch()}
                disabled={loading}
                className="shrink-0 rounded-xl bg-kilo-accent px-4 py-3 text-sm font-semibold text-black disabled:opacity-50"
              >
                {loading ? '…' : t('meals.unplanned.search')}
              </button>
            </div>
          ) : (
            <div className="mb-4">
              <input
                ref={barcodeInputRef}
                type="text"
                placeholder={t('meals.unplanned.barcodePlaceholder')}
                className={`w-full ${ONBOARDING.input}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    void handleBarcodeInput((e.target as HTMLInputElement).value)
                  }
                }}
              />
              <p className={`mt-2 text-center text-[11px] ${KILO.text.soft}`}>{t('meals.unplanned.barcodeHint')}</p>
            </div>
          )}

          {error ? <p className="mb-3 text-sm text-kilo-danger">{error}</p> : null}

          {results.length > 0 ? (
            <div className="space-y-2">
              <p className={KILO.fieldLabel}>{t('meals.unplanned.selectMatch')}</p>
              {results.map((result, i) => (
                <button
                  key={`${result.food_name}-${i}`}
                  type="button"
                  onClick={() => selectResult(result)}
                  className={`w-full rounded-xl border border-kilo-line bg-kilo-nested p-3 text-left transition-colors hover:border-kilo-accent/40`}
                >
                  <p className={`text-sm font-medium ${KILO.text.primary}`}>{result.food_name}</p>
                  {result.off_product_name && result.off_product_name !== result.food_name ? (
                    <p className={`mt-0.5 text-xs ${KILO.text.muted}`}>{result.off_product_name}</p>
                  ) : null}
                  <div className={`mt-1.5 flex flex-wrap gap-3 text-xs ${KILO.text.muted}`}>
                    <span>P: {result.protein_100g}g</span>
                    <span>C: {result.carbs_100g}g</span>
                    <span>F: {result.fat_100g}g</span>
                    <span>{result.energy_kcal_100g} kcal</span>
                    <span className={KILO.text.soft}>{t('meals.unplanned.per100g')}</span>
                  </div>
                </button>
              ))}
            </div>
          ) : null}
        </>
      ) : selected ? (
        <>
          <button
            type="button"
            onClick={() => {
              setStep('search')
              setSelected(null)
            }}
            className={`mb-4 text-xs ${KILO.text.muted}`}
          >
            {t('meals.unplanned.backToSearch')}
          </button>

          <div className={`mb-4 rounded-xl border border-kilo-line bg-kilo-nested p-3`}>
            <p className={`font-semibold ${KILO.text.primary}`}>{selected.food_name}</p>
            {selected.off_product_name ? (
              <p className={`mt-0.5 text-xs ${KILO.text.muted}`}>{selected.off_product_name}</p>
            ) : null}
            <p className={`mt-1 text-xs ${KILO.text.soft}`}>
              {selected.data_source === 'cached'
                ? t('meals.unplanned.sourceCached')
                : t('meals.unplanned.sourceOff')}
            </p>
          </div>

          <label className={`mb-4 block ${ONBOARDING.fieldGroup}`}>
            <span className={ONBOARDING.label}>{t('meals.unplanned.quantityLabel')}</span>
            <div className="flex items-center gap-3">
              <input
                type="number"
                min={1}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className={`w-28 text-center ${ONBOARDING.input}`}
              />
              <span className={`text-sm ${KILO.text.muted}`}>{t('meals.unplanned.grams')}</span>
            </div>
          </label>

          {computedMacros ? (
            <div className="mb-4 grid grid-cols-4 gap-2">
              {[
                { label: 'kcal', value: computedMacros.kcal },
                { label: t('meals.macros.protein'), value: `${computedMacros.protein}g` },
                { label: t('meals.macros.carbs'), value: `${computedMacros.carbs}g` },
                { label: t('meals.macros.fats'), value: `${computedMacros.fat}g` },
              ].map((macro) => (
                <div key={macro.label} className={`rounded-xl border border-kilo-line bg-kilo-nested p-2 text-center`}>
                  <p className={`text-sm font-bold ${KILO.text.primary}`}>{macro.value}</p>
                  <p className={`mt-0.5 text-[10px] ${KILO.text.muted}`}>{macro.label}</p>
                </div>
              ))}
            </div>
          ) : null}

          <div className="mb-5">
            <p className={`mb-2 ${ONBOARDING.label}`}>{t('meals.unplanned.mealTypeLabel')}</p>
            <div className="grid grid-cols-4 gap-1.5">
              {MEAL_TYPES.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMealType(m)}
                  className={`rounded-xl border px-1 py-2 text-xs font-medium capitalize transition ${
                    mealType === m ? ONBOARDING.chipActive : ONBOARDING.chipInactive
                  }`}
                >
                  {t(`meals.unplanned.mealTypes.${m}`)}
                </button>
              ))}
            </div>
          </div>

          {error ? <p className="mb-3 text-sm text-kilo-danger">{error}</p> : null}

          {logged ? (
            <div className="py-4 text-center">
              <p className="font-semibold text-kilo-accent">{t('meals.unplanned.loggedSuccess')}</p>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => void handleConfirm()}
              disabled={loading}
              className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl bg-kilo-accent text-sm font-semibold text-black disabled:opacity-50"
            >
              <MaterialIcon name="check" size="sm" />
              {loading
                ? t('meals.unplanned.saving')
                : t('meals.unplanned.logAction', { quantity, food: selected.food_name })}
            </button>
          )}

          <div className="mt-3">
            <MealWellnessDisclaimer />
          </div>
        </>
      ) : null}
    </BottomSheet>
  )
}

export default UnplannedMealLogger
